import React from 'react'
import { useForm } from 'react-hook-form'
import { useQueryClient } from 'react-query'
import {
  Header,
  Button,
  Loader,
  Stack,
  Text,
  Select,
  Label,
  Input,
  HTMLText,
} from '@crossfit/ui'

import { useAuth } from 'components/Auth'
import * as formsService from 'services/forms'
import Modal from 'components/Modal'
import style from './style.module.css'
import { useSettings } from 'components/Settings'
import { useAffiliate } from 'hooks/useAffiliate'

const SOFTWARE_OPTIONS = [
  { value: 'beyond_the_whiteboard', label: 'Beyond the Whiteboard' },
  { value: 'wodify', label: 'Wodify' },
  { value: 'sugar_wod', label: 'SugarWOD' },
  { value: 'boxmate', label: 'BoxMate' },
  { value: 'trainheroic', label: 'TrainHeroic' },
  { value: 'other', label: 'Other' },
  { value: 'none', label: 'None' },
]

function useWelcomeForm() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { selectedAffiliate: affiliate } = useAffiliate()
  const form = useForm()

  async function handleSubmit(data) {
    await formsService.submitProgrammingWaiver({
      data,
      user,
      affiliate,
    })
    await queryClient.refetchQueries('subscriptions')
    await queryClient.resetQueries('promotion')
  }

  return {
    ...form,
    onSubmit: form.handleSubmit(handleSubmit),
    isSubmitting: form.formState.isSubmitting,
    isSubmitSuccessful: form.formState.isSubmitSuccessful,
  }
}

function WelcomeFormModal({ isCoach }: { isCoach: boolean }) {
  const { onSubmit, isSubmitting, register, watch } = useWelcomeForm()
  const disabled =
    !watch('numberOfMembers') ||
    !watch('software') ||
    !watch('currentProgramming')
  const settings = useSettings((s) => s.data)

  return (
    <Modal
      canBeDismissed={false}
      header={
        <Header size='small' as='h3'>
          Welcome to CrossFit Affiliate Programming!
        </Header>
      }
    >
      {isSubmitting ? (
        <Loader />
      ) : (
        <>
          {isCoach ? (
            <HTMLText className={style.coachText}>
              {settings.programming.surveyRequiredForNewCoaches}
            </HTMLText>
          ) : (
            <>
              {' '}
              <Text mb={[8, 5]}>
                Almost there! Before diving in, tell us a bit more about your
                gym.
              </Text>
              <form onSubmit={onSubmit} className={style.form}>
                <Stack spacing={2} className={style.fieldGroup} mb={[8, 5]}>
                  <Label htmlFor='numberOfMembers' className={style.label}>
                    How many members are you programming for (approximately)?
                  </Label>
                  <Input
                    id='numberOfMembers'
                    type='number'
                    min='0'
                    placeholder='Number of members'
                    required
                    {...register('numberOfMembers')}
                  />
                </Stack>

                <Stack spacing={2} className={style.fieldGroup} mb={6}>
                  <Label htmlFor='software' className={style.label}>
                    What workout tracking software do you use at your affiliate?
                  </Label>
                  <Select id='software' required {...register('software')}>
                    <option></option>
                    {SOFTWARE_OPTIONS.map(({ label, value }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </Stack>

                <Stack spacing={2} className={style.fieldGroup} mb={[8, 5]}>
                  <Label htmlFor='currentProgramming' className={style.label}>
                    What programming services do you use currently?
                  </Label>
                  <Input
                    id='currentProgramming'
                    type='text'
                    required
                    {...register('currentProgramming')}
                  />
                </Stack>

                <Button type='submit' disabled={disabled}>
                  Enter
                </Button>
              </form>
            </>
          )}
        </>
      )}
    </Modal>
  )
}

export default WelcomeFormModal
